import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ArrowLeft, Navigation, MessageSquare, Phone } from 'lucide-react';
import { api } from '../services/api';
import { fmtClock } from '../utils/time';
import VisitHours from '../components/VisitHours';
import WalkthroughFinish from '../components/WalkthroughFinish';

function money(n: any) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${Math.round(v).toLocaleString('en-US')}` : '—';
}

export default function QuoteVisit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery(['quote-visit', id], () => api.get(`/quotes/visits/${id}`).then(r => r.data), { retry: false });
  const visit = data?.visit;
  const plans: any[] = data?.plans || [];

  const hours = useMutation((body: any) => api.put(`/quotes/visits/${id}`, body), {
    // The Field page shows this visit's time and plans the day around it: its
    // lists and its clock are asked again, so going back there never shows the
    // old hours (owner, Oct 5 2026: "mario has in between 7-10 but stuck at 7").
    onSuccess: () => {
      toast.success('Hours saved');
      qc.invalidateQueries(['quote-visit', id]); qc.invalidateQueries('calendar-jobs');
      qc.invalidateQueries('bid-visits'); qc.invalidateQueries('field-stops'); qc.invalidateQueries('day-clock');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save those hours'); },
  });
  const save = useMutation((body: any) => api.put(`/quotes/visits/${id}`, body), {
    onSuccess: (_d, body) => {
      toast.success(body.status === 'skipped' ? 'Visit cancelled' : 'Visit updated');
      qc.invalidateQueries(['quote-visit', id]); qc.invalidateQueries('calendar-jobs'); qc.invalidateQueries('bid-visits');
      qc.invalidateQueries('field-stops'); qc.invalidateQueries('day-clock');
      if (body.status === 'skipped') navigate('/field');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not update that visit'); },
  });

  const [day, setDay] = useState('');
  React.useEffect(() => { if (visit?.date) setDay(visit.date); }, [visit?.date]);

  if (isLoading) return <div className="py-12 text-center text-gray-500">Loading…</div>;
  if (error || !visit) return (
    <div className="py-12 text-center">
      <p className="text-red-600 mb-4">Could not load that visit.</p>
      <Link to="/field" className="text-brand-700 font-semibold">Back to Field</Link>
    </div>
  );

  const maps = visit.address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([visit.address, visit.city].filter(Boolean).join(', '))}`
    : null;
  const phone = visit.text_phone || visit.phone;
  const dayLabel = visit.date
    ? new Date(`${visit.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';
  const when = visit.hours?.exact != null ? fmtClock(visit.time_start)
    : visit.hours && !visit.hours.any
      ? [visit.hours.earliest != null ? fmtClock(`${String(Math.floor(visit.hours.earliest / 60)).padStart(2, '0')}:${String(visit.hours.earliest % 60).padStart(2, '0')}`) : null,
         visit.hours.latest != null ? fmtClock(`${String(Math.floor(visit.hours.latest / 60)).padStart(2, '0')}:${String(visit.hours.latest % 60).padStart(2, '0')}`) : null]
          .filter(Boolean).join(' – ')
      : (visit.time_start ? fmtClock(visit.time_start) : 'Any time');
  const sqft = visit.sqft ? `${Number(visit.sqft).toLocaleString('en-US')} sq ft${visit.sqft_assumed ? ' (assumed)' : ''}` : 'Not measured yet';
  const grass = visit.grass_type ? `${visit.grass_type}${visit.grass_assumed ? ' (assumed)' : ''}` : 'Grass not set';

  function writeBid() {
    const drafts = (data?.quotes || []).filter((q: any) => q.status === 'draft' || q.status === 'sent');
    if (drafts[0]) navigate('/quotes', { state: { openQuoteId: drafts[0].id } });
    else if (visit.latest_quote_id && (visit.latest_quote_status === 'draft' || visit.latest_quote_status === 'sent')) {
      navigate('/quotes', { state: { openQuoteId: visit.latest_quote_id } });
    } else {
      navigate('/quotes', { state: { prefillCustomerId: visit.customer_id, prefillPropertyId: visit.property_id } });
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <Link to="/field" className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Field</Link>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 mt-1">{String(visit.title || '').startsWith('Lawn walkthrough') ? 'Lawn walkthrough' : 'Quote visit'}</h1>
        <p className="text-xs text-gray-500">{dayLabel}{when ? ` · ${when}` : ''} · not a job</p>
      </div>

      <div className="card p-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-lg truncate">{visit.customer_name}</p>
            {visit.address && <p className="text-sm text-gray-600">{visit.address}{visit.city ? `, ${visit.city}` : ''}</p>}
            {phone && <p className="text-sm text-gray-700 mt-1">{phone}</p>}
          </div>
          <div className="flex gap-1.5 shrink-0">
            {maps && <a href={maps} target="_blank" rel="noreferrer" className="p-2 rounded-lg bg-brand-50 text-brand-700"><Navigation className="h-5 w-5" /></a>}
            {phone && !visit.sms_opt_out && <a href={`sms:${phone}`} className="p-2 rounded-lg bg-brand-50 text-brand-700"><MessageSquare className="h-5 w-5" /></a>}
            {phone && <a href={`tel:${phone}`} className="p-2 rounded-lg bg-brand-50 text-brand-700"><Phone className="h-5 w-5" /></a>}
          </div>
        </div>
        {(visit.gate_notes || visit.access_notes || visit.property_crew_notes) && (
          <p className="text-sm text-gray-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {[visit.gate_notes, visit.access_notes, visit.property_crew_notes].filter(Boolean).join(' · ')}
          </p>
        )}
        {visit.notes && <p className="text-sm text-gray-600">{visit.notes}</p>}
      </div>

      <div className="card p-4 space-y-3">
        <VisitHours value={visit.hours} saving={hours.isLoading} onSave={body => hours.mutate(body)} />
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs text-gray-600">Day
            <input type="date" className="input mt-1" value={day} onChange={e => setDay(e.target.value)} />
          </label>
          <button onClick={() => save.mutate({ date: day })} disabled={!day || day === visit.date || save.isLoading} className="btn-secondary text-xs disabled:opacity-50">Move the visit</button>
        </div>
      </div>

      <div className="card p-4 space-y-1">
        <p className="text-xs font-semibold text-gray-600">The lawn</p>
        <p className="text-sm text-gray-900">{sqft} · {grass}{Number(visit.front_only) === 1 ? ' · front only' : ''}</p>
        {visit.how_heard && <p className="text-sm text-gray-600">How they heard: {visit.how_heard}</p>}
        {visit.customer_notes && <p className="text-xs text-gray-500 line-clamp-3">{visit.customer_notes}</p>}
      </div>

      {plans.length > 0 && (
        <div className="card p-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">Plan prices for this yard</p>
          <div className="grid grid-cols-3 gap-2">
            {plans.map((p: any) => (
              <div key={p.plan} className="rounded-lg border border-gray-200 p-2 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{p.name}</p>
                <p className="text-lg font-black text-gray-900">{money(p.month)}<span className="text-xs font-semibold text-gray-500">/mo</span></p>
              </div>
            ))}
          </div>
          {visit.sqft_assumed && <p className="text-[11px] text-gray-500 mt-2">Lawn size assumed — type the square footage on the bid if you measure it.</p>}
        </div>
      )}

      {/* A lawn walkthrough's close-out: what he recommended, what he sends, the reminder (components/WalkthroughFinish). Null on a plain quote visit. */}
      <WalkthroughFinish jobId={id!} />

      <button onClick={writeBid} className="btn-primary w-full justify-center text-sm py-3">
        Write the bid
      </button>

      {visit.status === 'scheduled' && (
        <button
          onClick={() => { if (window.confirm(`Cancel the quote visit with ${visit.customer_name}?`)) save.mutate({ status: 'skipped' }); }}
          disabled={save.isLoading}
          className="w-full py-2.5 text-sm font-semibold text-red-700 border border-red-200 rounded-xl hover:bg-red-50">
          Cancel this visit
        </button>
      )}
    </div>
  );
}
